import i18n from "i18next";
import { useAtom, useAtomValue } from "jotai";
import { userPeriodFilter } from "../state/company";
import {
	ThemeMode,
	UserLocalNavigationPreferences,
	UserLocalPreferences,
	userLocalPreferences,
} from "../state/preferences";

/**
 * Hook usePreferences exports all states and functions required to manage the current user local preferences.
 */
export const usePreferences = () => {
	const [preferences, setPreferences] = useAtom(userLocalPreferences);

	const selectLanguage = (language?: string | null) => {
		const validLanguage = language || undefined;
		i18n.changeLanguage(language ?? preferences.browserLanguage);
		setPreferences({ ...preferences, language: validLanguage });
	};

	const changeTheme = (theme: ThemeMode) => {
		storePreferences({ ...preferences, theme });
	};

	const storePreferences = (newPreferences: UserLocalPreferences) => {
		setPreferences(newPreferences);
	};

	return {
		preferences,
		selectLanguage,
		changeTheme,
	};
};

/**
 * Hook useNavigationPreferences exports states and functions to manage the user's local navigation preferences.
 */
export const useNavigationPreferences = () => {
	const [preferences, setPreferences] = useAtom(userLocalPreferences);

	const setNavigationPreferences = (update: Partial<UserLocalNavigationPreferences>) => {
		setPreferences({
			...preferences,
			navigationPreferences: { ...preferences.navigationPreferences, ...update },
		});
	};

	const navigationPreferences: UserLocalNavigationPreferences = preferences.navigationPreferences ?? {
		transportsPerspective: "operational",
	};

	return {
		navigationPreferences,
		setNavigationPreferences,
	};
};

/**
 * Hook usePeriodFilter exports all states and functions required to manage the current user period filter (fiscal, custom).
 */
export const usePeriodFilter = () => {
	const currentPeriod = useAtomValue(userPeriodFilter);

	const isPeriodReady = !!currentPeriod?.start && !!currentPeriod?.end;

	const from = currentPeriod?.start;
	const to = currentPeriod?.end;

	return {
		filterFrom: from,
		formattedFrom: from?.format("YYYY-MM-DD") ?? "",
		filterTo: to,
		formattedTo: to?.format("YYYY-MM-DD") ?? "",
		isPeriodReady,
	};
};
